import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import {
  deleteAccount,
  exportMyData,
  updateConsents,
  type UpdateConsentsPayload,
} from "@/api/me";
import { ApiError } from "@/api/client";
import { useAuthStatus, useClearAuth, useInvalidateAuth } from "@/hooks/useAuth";

/**
 * Page Mon compte.
 *
 * V1 : modification des consentements (toggles), suppression du compte (RGPD).
 * Le changement de mot de passe arrivera dans une itération suivante.
 */
export default function AccountPage() {
  const { user } = useAuthStatus();
  const invalidateAuth = useInvalidateAuth();
  const clearAuth = useClearAuth();
  const navigate = useNavigate();

  const [marketing, setMarketing] = useState(user?.marketing_emails_opt_in ?? false);
  const [analytics, setAnalytics] = useState(user?.analytics_opt_in ?? false);
  const [ads, setAds] = useState(user?.ads_opt_in ?? false);

  const consentMutation = useMutation({
    mutationFn: (payload: UpdateConsentsPayload) => updateConsents(payload),
    onSuccess: () => invalidateAuth(),
  });

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-6 py-12 space-y-12">
        <section>
          <h1 className="text-2xl font-bold">Mon compte</h1>
          <p className="mt-2 text-sm text-gray-600">Connecté en tant que {user?.email}</p>
        </section>

        <section aria-labelledby="consents-heading" className="rounded border border-gray-200 bg-white p-6">
          <h2 id="consents-heading" className="text-lg font-semibold">
            Préférences de consentement
          </h2>
          <p className="mt-1 text-xs text-gray-500">
            Vous pouvez modifier ces préférences à tout moment (RGPD).
          </p>

          <div className="mt-4 space-y-3">
            <ConsentToggle
              label="Recevoir des emails de conseils (marketing)"
              checked={marketing}
              onChange={setMarketing}
            />
            <ConsentToggle
              label="Autoriser les statistiques anonymes d'usage"
              checked={analytics}
              onChange={setAnalytics}
            />
            <ConsentToggle
              label="Voir des publicités contextuelles"
              checked={ads}
              onChange={setAds}
            />
          </div>

          <button
            type="button"
            onClick={() =>
              consentMutation.mutate({
                marketing_emails_opt_in: marketing,
                analytics_opt_in: analytics,
                ads_opt_in: ads,
              })
            }
            disabled={consentMutation.isPending}
            className="mt-6 rounded bg-blue-900 px-4 py-2 text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {consentMutation.isPending ? "Enregistrement…" : "Enregistrer"}
          </button>
          {consentMutation.isSuccess && (
            <p className="mt-3 text-sm text-green-700">Préférences enregistrées.</p>
          )}
        </section>

        <DataExportSection />

        <DeleteAccountSection
          onDeleted={() => {
            clearAuth();
            navigate("/", { replace: true });
          }}
        />
      </div>
    </Layout>
  );
}

function ConsentToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-3 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

function DataExportSection() {
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mutation = useMutation({
    mutationFn: () => exportMyData(),
    onSuccess: (data) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mes-donnees-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setDone(true);
      setError(null);
    },
    onError: (err: ApiError) => {
      if (err.status === 404) {
        setError(
          "Cette fonctionnalité n'est pas encore disponible sur le serveur. Elle sera activée prochainement."
        );
      } else {
        setError("Une erreur est survenue lors de l'export. Veuillez réessayer.");
      }
      setDone(false);
    },
  });

  return (
    <section
      aria-labelledby="export-heading"
      className="rounded border border-gray-200 bg-white p-6"
    >
      <h2 id="export-heading" className="text-lg font-semibold">
        Exporter mes données (RGPD)
      </h2>
      <p className="mt-1 text-sm text-gray-600">
        Téléchargez toutes vos données (compte, logements, estimations) au format
        JSON. Droit à la portabilité (RGPD art. 20).
      </p>
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="mt-4 rounded border border-blue-900 px-4 py-2 text-blue-900 hover:bg-blue-50 disabled:opacity-50"
      >
        {mutation.isPending ? "Export en cours…" : "Télécharger mes données"}
      </button>
      {error && (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {error}
        </p>
      )}
      {done && !error && (
        <p className="mt-3 text-sm text-green-700">
          Export téléchargé avec succès.
        </p>
      )}
    </section>
  );
}

function DeleteAccountSection({ onDeleted }: { onDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (pw: string) => deleteAccount(pw),
    onSuccess: onDeleted,
    onError: (err: ApiError) => {
      if (err.code === "INVALID_CREDENTIALS") {
        setError("Mot de passe incorrect.");
      } else {
        setError("Une erreur est survenue, veuillez réessayer.");
      }
    },
  });

  return (
    <section
      aria-labelledby="delete-heading"
      className="rounded border border-red-200 bg-red-50 p-6"
    >
      <h2 id="delete-heading" className="text-lg font-semibold text-red-900">
        Supprimer mon compte
      </h2>
      <p className="mt-1 text-sm text-red-800">
        Cette action est définitive. Tous vos logements et estimations seront supprimés
        sous 30 jours.
      </p>

      {!confirming ? (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="mt-4 rounded border border-red-700 px-4 py-2 text-red-700 hover:bg-red-100"
        >
          Je veux supprimer mon compte
        </button>
      ) : (
        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            mutation.mutate(password);
          }}
        >
          <label htmlFor="del-pw" className="block text-sm font-medium">
            Confirmez avec votre mot de passe :
          </label>
          <input
            id="del-pw"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-2"
          />
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={mutation.isPending || password.length === 0}
              className="rounded bg-red-700 px-4 py-2 text-white hover:bg-red-800 disabled:opacity-50"
            >
              {mutation.isPending ? "Suppression…" : "Supprimer définitivement"}
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirming(false);
                setPassword("");
                setError(null);
              }}
              className="rounded border border-gray-300 px-4 py-2 hover:bg-gray-100"
            >
              Annuler
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
